/**
 * FAQ page progressive enhancement.
 * Builds topic TOC, converts H3 Q&A blocks into native <details>,
 * injects approved CTAs, and hides visible [VIDEO] placeholders.
 */
(function () {
  const root = document.querySelector('[data-faq-page]');
  if (!root) return;

  const content = root.querySelector('[data-faq-content]');
  const tocNav = root.querySelector('[data-faq-toc]');
  const tocList = root.querySelector('[data-faq-toc-list]');
  const intro = root.querySelector('[data-faq-intro]');
  if (!content || !tocNav || !tocList) return;

  const TOC_LABELS = {
    'dtf-transfers': 'DTF Transfers',
    'artwork-file-setup': 'Artwork & File Setup',
    'ai-generated-artwork': 'AI-Generated Artwork',
    'gang-sheets': 'Gang Sheets',
    'turnaround-same-day-orders-pickup': 'Turnaround & Pickup',
    'turnaround-pickup': 'Turnaround & Pickup',
    shipping: 'Shipping',
    'pressing-application': 'Pressing & Application',
    'washing-care': 'Washing & Care',
    'storing-transfers': 'Storing Transfers',
    'order-problems-policies': 'Order Problems & Policies',
    'bulk-business-orders': 'Bulk & Business Orders',
    'video-help-tutorials': 'Video Help & Tutorials'
  };

  const TOC_ORDER = [
    'dtf-transfers',
    'artwork-file-setup',
    'ai-generated-artwork',
    'gang-sheets',
    'turnaround-same-day-orders-pickup',
    'shipping',
    'pressing-application',
    'washing-care',
    'storing-transfers',
    'order-problems-policies',
    'bulk-business-orders',
    'video-help-tutorials'
  ];

  const ARTWORK_CTA_QUESTIONS = [
    'will you remove the background from my artwork?',
    'will you fix my artwork automatically?',
    'can legends help clean up ai artwork?'
  ];

  function slugify(text) {
    return text
      .toLowerCase()
      .replace(/&amp;/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
  }

  function plainText(node) {
    return (node.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function uniqueId(base, used) {
    let id = base || 'faq-section';
    let n = 2;
    while (used.has(id) || document.getElementById(id)) {
      id = base + '-' + n;
      n += 1;
    }
    used.add(id);
    return id;
  }

  function isHeading(el, tag) {
    return el && el.nodeType === 1 && el.tagName === tag;
  }

  function cloneTemplate(id) {
    const tpl = document.getElementById(id);
    if (!tpl || !tpl.content) return null;
    return tpl.content.cloneNode(true);
  }

  function hideVideoPlaceholders(scope) {
    scope.querySelectorAll('strong, p, span').forEach((el) => {
      const text = plainText(el);
      if (!/^\[VIDEO:/i.test(text)) return;

      const slot = document.createElement('div');
      slot.className = 'faq-video-slot';
      slot.setAttribute('data-faq-video-slot', '');
      slot.hidden = true;
      slot.setAttribute('aria-hidden', 'true');

      const target = el.closest('p') || el;
      target.replaceWith(slot);
    });
  }

  function enhanceVideos(scope) {
    scope.querySelectorAll('[data-faq-video], .faq-video').forEach((wrap) => {
      const iframe = wrap.querySelector('iframe');
      if (!iframe) return;
      if (!iframe.hasAttribute('loading')) iframe.setAttribute('loading', 'lazy');
      if (!wrap.classList.contains('faq-video')) wrap.classList.add('faq-video');
      if (!wrap.querySelector('.faq-video__frame') && iframe.parentElement === wrap) {
        const frame = document.createElement('div');
        frame.className = 'faq-video__frame';
        wrap.appendChild(frame);
        frame.appendChild(iframe);
      }
    });
  }

  // Move leading intro (before first H2) into intro region; drop duplicate H1.
  const usedIds = new Set();
  const leading = [];
  while (content.firstChild) {
    const node = content.firstChild;
    if (node.nodeType === 3 && !node.textContent.trim()) {
      content.removeChild(node);
      continue;
    }
    if (isHeading(node, 'H1')) {
      content.removeChild(node);
      continue;
    }
    if (isHeading(node, 'H2')) break;
    leading.push(node);
    content.removeChild(node);
  }
  if (intro) {
    leading.forEach((node) => intro.appendChild(node));
  }

  const children = Array.from(content.childNodes);
  const fragment = document.createDocumentFragment();
  let i = 0;

  while (i < children.length) {
    const node = children[i];

    if (isHeading(node, 'H2')) {
      const sectionTitle = plainText(node);
      const sectionId = uniqueId(slugify(sectionTitle), usedIds);
      node.id = sectionId;
      fragment.appendChild(node);
      i += 1;

      // Section intro content until first H3/H2.
      const introWrap = document.createElement('div');
      introWrap.className = 'faq-section-intro';
      while (i < children.length && !isHeading(children[i], 'H2') && !isHeading(children[i], 'H3')) {
        introWrap.appendChild(children[i]);
        i += 1;
      }
      if (introWrap.childNodes.length) {
        hideVideoPlaceholders(introWrap);
        fragment.appendChild(introWrap);
      }

      if (sectionId.indexOf('gang-sheet') === 0 || sectionTitle.toLowerCase().indexOf('gang sheet') === 0) {
        const gangCta = cloneTemplate('FaqGangSheetCtaTemplate');
        if (gangCta) fragment.appendChild(gangCta);
      }

      // Questions in this section.
      while (i < children.length && !isHeading(children[i], 'H2')) {
        if (!isHeading(children[i], 'H3')) {
          fragment.appendChild(children[i]);
          i += 1;
          continue;
        }

        const question = children[i];
        const questionText = plainText(question);
        const questionId = uniqueId(slugify(questionText), usedIds);
        i += 1;

        const body = document.createElement('div');
        body.className = 'faq-accordion__body rte';
        while (i < children.length && !isHeading(children[i], 'H2') && !isHeading(children[i], 'H3')) {
          body.appendChild(children[i]);
          i += 1;
        }

        hideVideoPlaceholders(body);
        enhanceVideos(body);

        const normalized = questionText.toLowerCase();
        if (ARTWORK_CTA_QUESTIONS.indexOf(normalized) !== -1) {
          const artworkCta = cloneTemplate('FaqArtworkCtaTemplate');
          if (artworkCta) body.appendChild(artworkCta);
        }

        const details = document.createElement('details');
        details.className = 'faq-accordion';
        details.id = questionId;

        const summary = document.createElement('summary');
        summary.textContent = questionText;

        details.appendChild(summary);
        details.appendChild(body);
        fragment.appendChild(details);
      }
      continue;
    }

    fragment.appendChild(node);
    i += 1;
  }

  content.innerHTML = '';
  content.appendChild(fragment);

  // Final sweep: catch any [VIDEO] placeholders outside accordion bodies.
  hideVideoPlaceholders(content);
  enhanceVideos(content);

  // Build TOC from enhanced H2s using preferred labels/order.
  const sections = Array.from(content.querySelectorAll('h2[id]'));
  const byId = {};
  sections.forEach((h2) => {
    byId[h2.id] = h2;
  });

  const ordered = [];
  TOC_ORDER.forEach((id) => {
    if (byId[id]) ordered.push(byId[id]);
  });
  sections.forEach((h2) => {
    if (ordered.indexOf(h2) === -1 && h2.id.indexOf('still-have-questions') === -1) {
      ordered.push(h2);
    }
  });

  ordered.forEach((h2) => {
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.className = 'faq-toc__link';
    a.href = '#' + h2.id;
    a.textContent = TOC_LABELS[h2.id] || plainText(h2);
    li.appendChild(a);
    tocList.appendChild(li);
  });

  if (tocList.children.length) {
    tocNav.hidden = false;
  }

  function openHashTarget() {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return;
    const target = document.getElementById(hash);
    if (!target) return;
    if (target.matches('details.faq-accordion')) {
      target.open = true;
    }
    const parentDetails = target.closest('details.faq-accordion');
    if (parentDetails) parentDetails.open = true;
  }

  openHashTarget();
  window.addEventListener('hashchange', openHashTarget);
})();
